import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { useFilterContainerClassName, useFilterLabelClassName } from 'utils/languageUtils';
import { Table, Button, Row, Col, Select, Drawer, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DateRangeFilterItem from '../../../../../delivery/components/DateRangeFilterItem';
import InputFilterItem from '../../../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../../../delivery/components/ParcelFilterStyle';
import { GET_PRICING_SCHEME_APPLY_LISTING } from '../graphql/queries/getPricingSchemeApplyList';
import { LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS, ListOperatorOptionsResponse } from '../graphql/queries/listOperatorOptions';
import CostPeriodicBindSchemeComp from './CostPeriodicBindSchemeComp';
import CostPeriodicUnbindSchemeComp from './CostPeriodicUnbindSchemeComp';
import CostPeriodicBindEditComp from './CostPeriodicBindEditComp';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { BUSINESS_SCENARIO_COST_PERIODIC } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { useEnumLabel } from 'hooks/useEnumLabel';
import { formatApplyTimeWithTimezone } from 'utils/timezoneFormatter';

interface ApplyItem {
  id: number;
  // 后端改成纯透传新服务 DTO 后，以下几个字段可能为空，不再由后端断言必填
  pricingSchemeId?: number | null;
  pricingSchemeName?: string | null;
  bindDimension?: string | null;
  bindValue?: string | null;
  bindValueDesc: string;
  effectiveTime?: string | null;
  expirationTime?: string | null;
  bindTime?: string | null;
  operatorName?: string | null;
  effectStatus: string;
  bindStatus: string;
  timezone: string;
}

type DrawerType = 'bind' | 'unbind' | 'edit';

interface Props {
  businessScenario: number | null;
}

const EFFECT_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: '1', label: '生效中' },
  { value: '0', label: '已失效' },
  { value: '2', label: '待生效' },
];

/** IANA 时区选项（Apply Price 仅支持 Asia/Shanghai、America/Los_Angeles） */
const TIMEZONE_OPTIONS = [
  { label: 'Asia/Shanghai', value: 'Asia/Shanghai' },
  { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
];

const DEFAULT_HALF_YEAR_RANGE: [string, string] = [
  dayjs().subtract(6, 'month').format('YYYY/MM/DD'),
  dayjs().format('YYYY/MM/DD'),
];

const CostPeriodicApplyPage: React.FC<Props> = ({ businessScenario }) => {
  const { t } = useTranslation();
  const est = useEnumLabel('enum.pricingApplyEffectStatus');
  const filterContainerClass = useFilterContainerClassName();
  const filterLabelClass = useFilterLabelClassName('w-[6em]');

  const [bindTimeRange, setBindTimeRange] = useState<[string | null, string | null] | null>(DEFAULT_HALF_YEAR_RANGE);
  const [effectiveTimeRange, setEffectiveTimeRange] = useState<[string | null, string | null] | null>(DEFAULT_HALF_YEAR_RANGE);
  const [operatorId, setOperatorId] = useState<string | undefined>(undefined);
  const [pricingSchemeId, setPricingSchemeId] = useState<string | undefined>(undefined);
  const [activeStatus, setActiveStatus] = useState<string>('');
  const [timezone, setTimezone] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [activeFilters, setActiveFilters] = useState({
    bindTime: DEFAULT_HALF_YEAR_RANGE as [string | null, string | null] | null,
    effectiveTime: DEFAULT_HALF_YEAR_RANGE as [string | null, string | null] | null,
    operatorId: undefined as string | undefined,
    pricingSchemeId: undefined as string | undefined,
    activeStatus: '',
    timezone: undefined as string | undefined,
  });

  const { data: operatorOptionsData, loading: operatorOptionsLoading, refetch: refetchOperatorOptions } = useQuery<ListOperatorOptionsResponse>(
    LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS,
    { variables: { businessScenario: businessScenario ?? undefined } }
  );
  const operatorOptions = operatorOptionsData?.listPricingSchemeApplyOperatorOptions || [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKey, setDrawerKey] = useState(0);
  const [drawerType, setDrawerType] = useState<DrawerType>('bind');

  const openDrawer = (type: DrawerType) => {
    setDrawerType(type);
    setDrawerKey(k => k + 1);
    setDrawerOpen(true);
  };

  const buildTimeRange = (range: [string | null, string | null] | null) => {
    if (!range?.[0] || !range?.[1]) return undefined;
    return { start: `${range[0]} 00:00:00`, end: `${range[1]} 23:59:59` };
  };

  const queryVariables = useMemo(() => ({
    filters: {
      businessScenario: BUSINESS_SCENARIO_COST_PERIODIC,
      pricingSchemeId: activeFilters.pricingSchemeId ? parseInt(activeFilters.pricingSchemeId, 10) : undefined,
      bindTime: buildTimeRange(activeFilters.bindTime),
      effectiveTime: buildTimeRange(activeFilters.effectiveTime),
      activeStatus: activeFilters.activeStatus || undefined,
      operatorId: activeFilters.operatorId ? parseInt(activeFilters.operatorId, 10) : undefined,
      timezone: activeFilters.timezone,
    },
    pageRequest: { page: currentPage, size: pageSize },
  }), [activeFilters, currentPage, pageSize]);

  const { data, loading, refetch } = useQuery(GET_PRICING_SCHEME_APPLY_LISTING, {
    variables: queryVariables,
    skip: !businessScenario,
    fetchPolicy: 'network-only',
  });

  const handleFilter = () => {
    setActiveFilters({ bindTime: bindTimeRange, effectiveTime: effectiveTimeRange, operatorId, pricingSchemeId, activeStatus, timezone });
    setCurrentPage(1);
  };

  const handleReset = () => {
    setBindTimeRange(DEFAULT_HALF_YEAR_RANGE);
    setEffectiveTimeRange(DEFAULT_HALF_YEAR_RANGE);
    setOperatorId(undefined);
    setPricingSchemeId(undefined);
    setActiveStatus('');
    setTimezone(undefined);
    setActiveFilters({ bindTime: DEFAULT_HALF_YEAR_RANGE, effectiveTime: DEFAULT_HALF_YEAR_RANGE, operatorId: undefined, pricingSchemeId: undefined, activeStatus: '', timezone: undefined });
    setCurrentPage(1);
  };

  const handleDrawerSuccess = () => {
    setDrawerOpen(false);
    handleFilter();
    refetch();
    refetchOperatorOptions();
  };

  const drawerTitle =
    drawerType === 'bind' ? '方案绑定/Apply Price' :
    drawerType === 'unbind' ? '方案解绑/Plan UnBind' :
    '绑定编辑/Bind Edit';

  const columns: ColumnsType<ApplyItem> = [
    { title: '方案ID', dataIndex: 'pricingSchemeId', width: 100 },
    { title: '方案名称', dataIndex: 'pricingSchemeName', width: 180, render: v => v ?? '-' },
    {
      title: '绑定时间',
      dataIndex: 'bindTime',
      width: 180,
      render: (v, r) => formatApplyTimeWithTimezone(v, r.timezone),
    },
    {
      title: '生效时间',
      width: 220,
      render: (_, r) => {
        const expiration = r.expirationTime ? formatApplyTimeWithTimezone(r.expirationTime, r.timezone) : '长期';
        return `${formatApplyTimeWithTimezone(r.effectiveTime, r.timezone)} - ${expiration}`;
      },
    },
    { title: '时区', dataIndex: 'timezone', width: 160, render: v => v ?? '-' },
    {
      title: '生效状态',
      dataIndex: 'effectStatus',
      width: 100,
      render: (status: string) => {
        const cfg: Record<string, { color: string }> = {
          'EFFECTIVE': { color: 'success' },
          'PENDING': { color: 'processing' },
          'EXPIRED': { color: 'default' },
          'UNBOUND': { color: 'default' },
        };
        const { color } = cfg[status] ?? { color: 'default' };
        // UNBOUND 归一化为 EXPIRED 展示：与老系统"已解绑记录展示为已失效"的口径保持一致
        return <Tag color={color}>{est(status === 'UNBOUND' ? 'EXPIRED' : status)}</Tag>;
      },
    },
    {
      title: '绑定状态',
      dataIndex: 'bindStatus',
      width: 100,
      // 后端现在吐原始 code（"1"-已绑定/"0"-已解绑），展示文案在前端维护
      render: (status: string) => {
        const cfg: Record<string, { color: string; text: string }> = {
          '1': { color: 'green', text: '已绑定' },
          '0': { color: 'red', text: '已解绑' },
        };
        const { color, text } = cfg[status] ?? { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    { title: '操作人', dataIndex: 'operatorName', width: 120, render: v => v ?? '-' },
    { title: '绑定范围', dataIndex: 'bindValueDesc', width: 120 },
  ];

  return (
    <div>
      <Row gutter={[16, 18]} wrap>
        {/* 第一行：操作人 + 方案ID + 生效状态 */}
        <Col xs={24} sm={24} md={8} lg={8}>
          <ParcelFilterStyle
            label="操作人"
            spanClassName="w-[6em]"
            options={operatorOptions}
            value={operatorId}
            onChange={(v: string) => setOperatorId(v || undefined)}
            disabled={operatorOptionsLoading}
            showSearch
            allowClear
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8}>
          <InputFilterItem label="方案ID" spanClassName="w-[6em]" value={pricingSchemeId} onChange={e => setPricingSchemeId(e.target.value || undefined)} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8}>
          <div className={filterContainerClass}>
            <span className={filterLabelClass}>生效状态</span>
            <Select style={{ flex: 1 }} options={EFFECT_STATUS_OPTIONS} value={activeStatus} onChange={setActiveStatus} />
          </div>
        </Col>
        {/* 第二行：时区 + 操作绑定时间 + 方案生效时间 */}
        <Col xs={24} sm={24} md={8} lg={8}>
          <ParcelFilterStyle
            label="时区"
            spanClassName="w-[6em]"
            options={TIMEZONE_OPTIONS}
            value={timezone ?? undefined}
            onChange={(v: string) => setTimezone(v ? v : undefined)}
          />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8}>
          <DateRangeFilterItem label="操作绑定时间" spanClassName="w-[8em]" value={bindTimeRange} onChange={setBindTimeRange} />
        </Col>
        <Col xs={24} sm={24} md={8} lg={8}>
          <DateRangeFilterItem label="方案生效时间" spanClassName="w-[8em]" value={effectiveTimeRange} onChange={setEffectiveTimeRange} />
        </Col>
        <Col span={24}>
          <Row justify="end">
            <Col>
              <div className="flex gap-3">
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_OPERATE]}>
                  <Button type="primary" onClick={() => openDrawer('bind')}>{t('settlement.priceCenter.actions.planBind')}</Button>
                  <Button type="primary" onClick={() => openDrawer('unbind')}>{t('settlement.priceCenter.actions.planUnbind')}</Button>
                  <Button type="primary" onClick={() => openDrawer('edit')}>{t('settlement.priceCenter.actions.bindEdit')}</Button>
                </HasResource>
                <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_CHECK]}>
                  <Button type="primary" onClick={handleFilter}>{t('settlement.priceCenter.actions.filter')}</Button>
                  <Button onClick={handleReset}>{t('settlement.priceCenter.actions.reset')}</Button>
                </HasResource>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={data?.getPricingSchemeApplyPageable?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.getPricingSchemeApplyPageable?.totalElements || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: total => t('table.pagination.totalItems', { total }),
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            onShowSizeChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
          }}
        />
      </div>

      <Drawer
        title={drawerTitle}
        width={800}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        maskClosable={false}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {drawerType === 'bind' && (
          <CostPeriodicBindSchemeComp key={drawerKey} onSuccess={handleDrawerSuccess} onCancel={() => setDrawerOpen(false)} />
        )}
        {drawerType === 'unbind' && (
          <CostPeriodicUnbindSchemeComp key={drawerKey} onSuccess={handleDrawerSuccess} onCancel={() => setDrawerOpen(false)} />
        )}
        {drawerType === 'edit' && (
          <CostPeriodicBindEditComp key={drawerKey} onSuccess={handleDrawerSuccess} onCancel={() => setDrawerOpen(false)} />
        )}
      </Drawer>
    </div>
  );
};

export default CostPeriodicApplyPage;
